import { useSDKActionWithDeps } from "@sdk/sdk.hooks";
import { iExternalAppConfig_TADesk } from "@sdk/user-management/user-management.models";
import { Alert, Button, Form, Input, Switch, Tooltip, message } from "antd";
import { HelpUsHelpYou } from "components/help-us-help-you/help-us-help-you";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setOrganization } from "store/modules/workspace/workspace.actions";
import { selectIntegrationConfig_TADesk } from "store/modules/workspace/workspace.selectors";
import { normalizeTaDeskDomain } from "./build-ta-desk-quote-url";

export const TADeskIntegrationConfig = () => {
  const integrationConfig = useSelector(selectIntegrationConfig_TADesk);
  const dispatch = useDispatch();

  const { doAction: completeIntegration, isProcessing: isSaving } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (data: iExternalAppConfig_TADesk & { apiKey?: string }) =>
          SDK.externalAccounts
            .completeIntegration({
              type: "TA_DESK",
              data: {
                domain: data.domain,
                apiKey: data.apiKey,
                enabled: data.enabled,
              },
            })
            .then((d) => dispatch(setOrganization(d))),
        successMessage: "TA Desk integration has been saved",
        failureMessage: "Something went wrong",
        actionDependencies: [],
      }),
      [dispatch],
    );

  const { doAction: removeIntegration, isProcessing: isRemoving } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => () =>
          SDK.externalAccounts
            .removeIntegration({
              type: "TA_DESK",
            })
            .then((d) => dispatch(setOrganization(d))),
        successMessage: "Integration has been removed",
        failureMessage: "Something went wrong",
        actionDependencies: [],
      }),
      [dispatch],
    );

  const [form] = Form.useForm();

  const onFinish = useCallback(async () => {
    try {
      const values = await form.validateFields();
      completeIntegration({
        ...values,
        domain: normalizeTaDeskDomain(values.domain),
        enabled: values.enabled ?? true,
      });
    } catch (e) {
      if ((e as { errorFields?: unknown })?.errorFields) {
        message.error("Please check your input");
      }
    }
  }, [completeIntegration, form]);

  const initialValues = {
    domain: integrationConfig?.config?.domain || "",
    apiKey: integrationConfig?.auth?.apiKey || "",
    enabled: integrationConfig?.config?.enabled ?? true,
  };

  return (
    <div className="w-full">
      {integrationConfig && (
        <div className="flex flex-row justify-between items-center mb-4">
          {integrationConfig.config?.enabled ? (
            <Alert
              message="TA Desk integration is active"
              type="success"
              showIcon
              className="flex-1"
            />
          ) : (
            <Alert
              message="TA Desk integration is configured but disabled"
              type="warning"
              showIcon
              className="flex-1"
            />
          )}
          <Tooltip title="Remove Integration">
            <Button
              type="link"
              icon={<i className="ri-delete-bin-line"></i>}
              onClick={() => {
                removeIntegration();
              }}
              loading={isRemoving}
            ></Button>
          </Tooltip>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        requiredMark={false}
        className="bold-form-labels"
        onFinish={onFinish}
      >
        <Form.Item
          name="domain"
          label="TA Desk Domain"
          rules={[
            {
              required: true,
              message: "Please enter your TA Desk domain",
            },
          ]}
        >
          <Input placeholder="acme-agency" addonAfter=".tadesk.app" />
        </Form.Item>
        <Form.Item
          name="apiKey"
          label="TA Desk API Key"
          rules={[
            {
              required: !integrationConfig?.auth?.apiKey,
              message: "Please enter your TA Desk API key",
            },
          ]}
        >
          <Input placeholder="API Key" type="password" />
        </Form.Item>
        <div className="flex flex-row items-center justify-between mb-4">
          <div className="font-bold">Enable TA Desk</div>
          <Form.Item
            name="enabled"
            valuePropName="checked"
            style={{ marginBottom: 0, marginLeft: 5 }}
          >
            <Switch />
          </Form.Item>
        </div>
        <div className="flex flex-row justify-end items-center">
          <Button
            type="primary"
            className="font-bold"
            loading={isSaving}
            onClick={onFinish}
          >
            Save
          </Button>
        </div>
      </Form>

      <HelpUsHelpYou
        chatMessage="How can we improve the TA Desk Integration for you?"
        emoji="✈️"
        description="Share your workflows with TA Desk and we'll ensure that our integration saves you time."
      />
    </div>
  );
};
